import React, { useState, useEffect } from 'react';
import {
  Typography,
  Button,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  CircularProgress,
  Alert,
  Snackbar,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Avatar,
  TextField,
  Drawer
} from '@mui/material';
import {
  Add as AddIcon,
  Refresh as RefreshIcon,
  LockReset as LockResetIcon,
  Block as BlockIcon,
  Menu as MenuIcon,
  CheckCircle as CheckCircleIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  People as PeopleIcon,
  BarChart as BarChartIcon,
  Logout as LogoutIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import CreateClientDialog from './CreateClientDialog';
import AdminUsageDashboard from './AdminUsageDashboard';
import { countries } from '../data/countries';
import axios from 'axios';

interface Client {
  id: string;
  email: string;
  twilioNumber: string;
  countryCode?: string;
  monthlySmsLimit?: number | null;
  monthlyCostLimit?: number | null;
  costMultiplier?: number;
  segmentsUsed?: number;
  createdAt: string;
  lastLogin: string | null;
  isActive: boolean;
  requiresPasswordReset: boolean;
}

interface PaginationInfo {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

const getCountryDisplay = (callingCode?: string): string => {
  if (!callingCode) return 'US (+1)';
  const country = countries.find(c => c.callingCode === callingCode);
  return country ? `${country.code} (+${country.callingCode})` : `(+${callingCode})`;
};

type AdminView = 'clients' | 'usage';

const AdminDashboard: React.FC = () => {
  const { user, logout } = useAuth();
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [activeView, setActiveView] = useState<AdminView>('clients');
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importForm, setImportForm] = useState({ email: '', twilioNumber: '', countryCode: 'CA', subaccountSid: '', authToken: '' });
  const [importLoading, setImportLoading] = useState(false);
  const [pagination, setPagination] = useState<PaginationInfo>({ total: 0, page: 1, limit: 10, totalPages: 0 });
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'info' }>({ open: false, message: '', severity: 'info' });
  const [confirmDialog, setConfirmDialog] = useState<{ open: boolean; title: string; message: string; onConfirm: () => void }>({ open: false, title: '', message: '', onConfirm: () => {} });
  const [editCountryDialog, setEditCountryDialog] = useState<{ open: boolean; client: Client | null; countryCode: string }>({ open: false, client: null, countryCode: '1' });
  const [smsLimitDialog, setSmsLimitDialog] = useState<{ open: boolean; client: Client | null; limit: string; multiplier: string }>({ open: false, client: null, limit: '', multiplier: '10' });

  useEffect(() => { loadClients(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.page, pagination.limit]);

  const loadClients = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/admin/clients', { params: { page: pagination.page, limit: pagination.limit } });
      setClients(response.data.clients);
      setPagination(response.data.pagination);
    } catch (error: any) { showSnackbar(error.response?.data?.error || 'Failed to load clients', 'error'); }
    finally { setLoading(false); }
  };

  const handleChangePage = (_event: unknown, newPage: number) => { setPagination(prev => ({ ...prev, page: newPage + 1 })); };
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => { setPagination(prev => ({ ...prev, limit: parseInt(event.target.value, 10), page: 1 })); };

  const handleResetPassword = (client: Client) => {
    setConfirmDialog({ open: true, title: 'Reset Password', message: `Reset password for ${client.email}? A new temporary password will be emailed.`,
      onConfirm: async () => { try { await axios.post(`/api/admin/clients/${client.id}/reset-password`); showSnackbar('Password reset successfully.', 'success'); loadClients(); } catch (e: any) { showSnackbar(e.response?.data?.error || 'Failed', 'error'); } setConfirmDialog(p => ({ ...p, open: false })); }
    });
  };

  const handleToggleActive = (client: Client) => {
    const action = client.isActive ? 'deactivate' : 'reactivate';
    setConfirmDialog({ open: true, title: client.isActive ? 'Deactivate Account' : 'Reactivate Account', message: `${action.charAt(0).toUpperCase() + action.slice(1)} ${client.email}?`,
      onConfirm: async () => { try { await axios.post(`/api/admin/clients/${client.id}/${action}`); showSnackbar(`Account ${action}d.`, 'success'); loadClients(); } catch (e: any) { showSnackbar(e.response?.data?.error || 'Failed', 'error'); } setConfirmDialog(p => ({ ...p, open: false })); }
    });
  };

  const handleDeleteClient = (client: Client) => {
    setConfirmDialog({ open: true, title: 'Delete Client', message: `Permanently delete ${client.email}? This cannot be undone.`,
      onConfirm: async () => { try { await axios.delete(`/api/admin/clients/${client.id}`); showSnackbar('Client deleted.', 'success'); loadClients(); } catch (e: any) { showSnackbar(e.response?.data?.error || 'Failed', 'error'); } setConfirmDialog(p => ({ ...p, open: false })); }
    });
  };

  const handleEditCountry = (client: Client) => {
    // Find the ISO code for this client's callingCode; default to 'US' if ambiguous
    const callingCode = client.countryCode || '1';
    const country = countries.find(c => c.callingCode === callingCode);
    setEditCountryDialog({ open: true, client, countryCode: country ? country.code : 'US' });
  };
  const handleEditCountryChange = (e: SelectChangeEvent<string>) => { setEditCountryDialog(prev => ({ ...prev, countryCode: e.target.value })); };
  const handleEditCountrySave = async () => {
    if (!editCountryDialog.client) return;
    // Convert ISO code back to callingCode for storage
    const country = countries.find(c => c.code === editCountryDialog.countryCode);
    const callingCode = country ? country.callingCode : '1';
    try { await axios.put(`/api/admin/clients/${editCountryDialog.client.id}`, { countryCode: callingCode }); showSnackbar('Country updated.', 'success'); setEditCountryDialog({ open: false, client: null, countryCode: 'US' }); loadClients(); }
    catch (e: any) { showSnackbar(e.response?.data?.error || 'Failed', 'error'); }
  };

  const handleEditSmsLimit = (client: Client) => { setSmsLimitDialog({ open: true, client, limit: client.monthlyCostLimit != null ? String(client.monthlyCostLimit) : '', multiplier: String(client.costMultiplier || 10) }); };
  const handleSmsLimitSave = async () => {
    if (!smsLimitDialog.client) return;
    const limit = smsLimitDialog.limit.trim() === '' ? null : parseFloat(smsLimitDialog.limit);
    if (limit !== null && (isNaN(limit) || limit < 0)) { showSnackbar('Limit must be a positive number or empty (unlimited)', 'error'); return; }
    const multiplier = parseFloat(smsLimitDialog.multiplier);
    if (isNaN(multiplier) || multiplier <= 0) { showSnackbar('Multiplier must be a positive number', 'error'); return; }
    try {
      await axios.put(`/api/admin/clients/${smsLimitDialog.client.id}/sms-limit`, { limit });
      await axios.put(`/api/admin/clients/${smsLimitDialog.client.id}/cost-multiplier`, { multiplier });
      showSnackbar(limit === null ? 'Cost limit removed (unlimited)' : `Cost limit set to $${limit}/mo at ${multiplier}x`, 'success');
      setSmsLimitDialog({ open: false, client: null, limit: '', multiplier: '10' }); loadClients();
    } catch (e: any) { showSnackbar(e.response?.data?.error || 'Failed', 'error'); }
  };

  const showSnackbar = (message: string, severity: 'success' | 'error' | 'info') => { setSnackbar({ open: true, message, severity }); };

  const navItems = [
    { id: 'clients' as AdminView, label: 'Clients', icon: <PeopleIcon /> },
    { id: 'usage' as AdminView, label: 'Usage', icon: <BarChartIcon /> },
  ];

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: '#f6f6f8', overflow: 'hidden', width: '100%' }}>
      {/* Mobile Drawer */}
      <Drawer open={mobileDrawerOpen} onClose={() => setMobileDrawerOpen(false)} sx={{ display: { xs: 'block', md: 'none' }, '& .MuiDrawer-paper': { width: 220 } }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          <Box sx={{ p: 3, pb: 2 }}>
            <Typography sx={{ fontFamily: 'Manrope, sans-serif', fontWeight: 800, fontSize: '1.125rem', color: '#0f172a' }}>Admin Panel</Typography>
            <Typography sx={{ fontFamily: 'Inter, sans-serif', color: '#475569', fontSize: '0.6875rem', textTransform: 'uppercase', letterSpacing: 1 }}>Super Admin</Typography>
          </Box>
          <Box sx={{ px: 1.5, flex: 1 }}>
            {navItems.map((item) => (
              <Box key={item.id} onClick={() => { setActiveView(item.id); setMobileDrawerOpen(false); }}
                sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1.2, borderRadius: 2, cursor: 'pointer', mb: 0.5, bgcolor: activeView === item.id ? 'rgba(75,43,238,0.10)' : 'transparent', color: activeView === item.id ? '#4b2bee' : '#475569', '&:hover': { bgcolor: activeView === item.id ? 'rgba(75,43,238,0.10)' : 'rgba(75,43,238,0.04)' } }}>
                {React.cloneElement(item.icon, { sx: { fontSize: 20 } })}
                <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.875rem', fontWeight: activeView === item.id ? 600 : 400 }}>{item.label}</Typography>
              </Box>
            ))}
          </Box>
          <Box sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Avatar sx={{ width: 32, height: 32, bgcolor: '#4b2bee', fontSize: 13 }}>A</Avatar>
              <Box sx={{ flex: 1, overflow: 'hidden' }}>
                <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.8125rem', fontWeight: 600, color: '#0f172a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{user?.email || 'Admin'}</Typography>
              </Box>
              <IconButton size="small" onClick={logout} sx={{ color: '#475569' }}><LogoutIcon fontSize="small" /></IconButton>
            </Box>
          </Box>
        </Box>
      </Drawer>
      {/* Desktop Sidebar */}
      <Box data-sidebar="desktop" sx={{ width: 220, flexShrink: 0, bgcolor: '#ffffff', position: 'fixed', top: 0, left: 0, bottom: 0, display: { xs: 'none', md: 'flex' }, flexDirection: 'column', zIndex: 1200 }}>
        <Box sx={{ p: 3, pb: 2 }}>
          <Typography sx={{ fontFamily: 'Manrope, sans-serif', fontWeight: 800, fontSize: '1.125rem', color: '#0f172a' }}>Admin Panel</Typography>
          <Typography sx={{ fontFamily: 'Inter, sans-serif', color: '#475569', fontSize: '0.6875rem', textTransform: 'uppercase', letterSpacing: 1 }}>Super Admin</Typography>
        </Box>

        <Box sx={{ px: 1.5, flex: 1 }}>
          {navItems.map((item) => (
            <Box
              key={item.id}
              onClick={() => setActiveView(item.id)}
              sx={{
                display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1.2, borderRadius: 2, cursor: 'pointer', mb: 0.5,
                bgcolor: activeView === item.id ? 'rgba(75,43,238,0.10)' : 'transparent',
                color: activeView === item.id ? '#4b2bee' : '#475569',
                '&:hover': { bgcolor: activeView === item.id ? 'rgba(75,43,238,0.10)' : 'rgba(75,43,238,0.04)' },
              }}
            >
              {React.cloneElement(item.icon, { sx: { fontSize: 20 } })}
              <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.875rem', fontWeight: activeView === item.id ? 600 : 400 }}>{item.label}</Typography>
            </Box>
          ))}
        </Box>

        <Box sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Avatar sx={{ width: 32, height: 32, bgcolor: '#4b2bee', fontSize: 13 }}>A</Avatar>
            <Box sx={{ flex: 1, overflow: 'hidden' }}>
              <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.8125rem', fontWeight: 600, color: '#0f172a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{user?.email || 'Admin'}</Typography>
            </Box>
            <IconButton size="small" onClick={logout} sx={{ color: '#475569' }}><LogoutIcon fontSize="small" /></IconButton>
          </Box>
        </Box>
      </Box>

      {/* Main */}
      <Box sx={{ flex: 1, ml: { xs: 0, md: '220px' }, p: { xs: 1.5, md: 4 }, minWidth: 0, maxWidth: '100%', overflowX: 'hidden', bgcolor: '#f6f6f8' }}>
        {/* Mobile top bar */}
        <Box sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center', mb: 2 }}>
          <IconButton onClick={() => setMobileDrawerOpen(true)} sx={{ color: '#475569', mr: 1 }}><MenuIcon /></IconButton>
          <Typography sx={{ fontFamily: 'Manrope, sans-serif', fontWeight: 700, fontSize: '1.125rem', color: '#0f172a' }}>Admin Panel</Typography>
        </Box>
        {activeView === 'clients' && (
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 1.5 }}>
              <Typography sx={{ fontFamily: 'Manrope, sans-serif', fontWeight: 800, fontSize: { xs: '1.25rem', md: '1.75rem' }, color: '#0f172a' }}>Client Management</Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <IconButton onClick={loadClients} disabled={loading} sx={{ color: '#475569' }}><RefreshIcon /></IconButton>
                <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateDialogOpen(true)}
                  sx={{ bgcolor: '#4b2bee', borderRadius: 2, textTransform: 'none', fontFamily: 'Inter, sans-serif', fontWeight: 600, px: 3, '&:hover': { bgcolor: '#3d1fd4' } }}
                >Add Client</Button>
                <Button variant="outlined" onClick={() => setImportDialogOpen(true)}
                  sx={{ borderColor: '#4b2bee', color: '#4b2bee', borderRadius: 2, textTransform: 'none', fontFamily: 'Inter, sans-serif', fontWeight: 600, px: 3, '&:hover': { bgcolor: 'rgba(75,43,238,0.04)', borderColor: '#4b2bee' } }}
                >Import Existing</Button>
              </Box>
            </Box>

            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}><CircularProgress sx={{ color: '#4b2bee' }} /></Box>
            ) : clients.length === 0 ? (
              <Alert severity="info" sx={{ borderRadius: 2 }}>No clients found.</Alert>
            ) : (
              <Box sx={{ bgcolor: '#ffffff', borderRadius: 3, boxShadow: '0px 12px 32px rgba(15, 23, 42, 0.04)', overflow: 'hidden', maxWidth: '100%' }}>
                <TableContainer sx={{ maxWidth: '100%', overflowX: 'auto' }}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        {['Email', 'Twilio Number', 'Country', 'SMS Limit', 'Status', 'Actions'].map((h) => (
                          <TableCell key={h} align={h === 'Actions' ? 'right' : 'left'} sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: '0.6875rem', textTransform: 'uppercase', color: '#475569', letterSpacing: 0.5 }}>{h}</TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {clients.map((client) => (
                        <TableRow key={client.id} sx={{ '&:hover': { bgcolor: 'rgba(75,43,238,0.04)' } }}>
                          <TableCell sx={{ fontFamily: 'Inter, sans-serif', color: '#0f172a', fontWeight: 500 }}>{client.email}</TableCell>
                          <TableCell sx={{ fontFamily: 'Inter, sans-serif', color: '#475569' }}>{client.twilioNumber}</TableCell>
                          <TableCell sx={{ fontFamily: 'Inter, sans-serif', color: '#475569' }}>{getCountryDisplay(client.countryCode)}</TableCell>
                          <TableCell>
                            <Chip
                              label={client.monthlyCostLimit != null ? `$${client.monthlyCostLimit} (${client.costMultiplier || 10}x)` : 'Unlimited'}
                              size="small"
                              onClick={() => handleEditSmsLimit(client)}
                              sx={{ bgcolor: client.monthlyCostLimit != null ? 'rgba(75,43,238,0.1)' : '#f1f5f9', color: client.monthlyCostLimit != null ? '#4b2bee' : '#475569', fontWeight: 600, fontSize: '0.6875rem', cursor: 'pointer' }}
                            />
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              <Chip label={client.isActive ? 'Active' : 'Inactive'} size="small"
                                sx={{ bgcolor: client.isActive ? 'rgba(0,184,148,0.1)' : '#f1f5f9', color: client.isActive ? '#00b894' : '#475569', fontWeight: 600, fontSize: '0.6875rem' }} />
                              {client.requiresPasswordReset && <Chip label="Reset" size="small" sx={{ bgcolor: 'rgba(225,112,85,0.1)', color: '#e17055', fontWeight: 600, fontSize: '0.6875rem' }} />}
                            </Box>
                          </TableCell>
                          <TableCell align="right">
                            <Tooltip title="Edit Country"><IconButton size="small" onClick={() => handleEditCountry(client)} sx={{ color: '#475569' }}><EditIcon fontSize="small" /></IconButton></Tooltip>
                            <Tooltip title="Reset Password"><IconButton size="small" onClick={() => handleResetPassword(client)} sx={{ color: '#4b2bee' }}><LockResetIcon fontSize="small" /></IconButton></Tooltip>
                            <Tooltip title={client.isActive ? 'Deactivate' : 'Reactivate'}><IconButton size="small" onClick={() => handleToggleActive(client)} sx={{ color: client.isActive ? '#e17055' : '#00b894' }}>{client.isActive ? <BlockIcon fontSize="small" /> : <CheckCircleIcon fontSize="small" />}</IconButton></Tooltip>
                            <Tooltip title="Delete"><IconButton size="small" onClick={() => handleDeleteClient(client)} sx={{ color: '#e17055' }}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination rowsPerPageOptions={[5, 10, 25]} component="div" count={pagination.total} rowsPerPage={pagination.limit} page={pagination.page - 1} onPageChange={handleChangePage} onRowsPerPageChange={handleChangeRowsPerPage} />
              </Box>
            )}
          </Box>
        )}

        {activeView === 'usage' && <AdminUsageDashboard />}
      </Box>

      {/* Dialogs */}
      <CreateClientDialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} onSuccess={loadClients} />

      <Dialog open={editCountryDialog.open} onClose={() => setEditCountryDialog({ open: false, client: null, countryCode: '1' })} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontFamily: 'Manrope, sans-serif', fontWeight: 700 }}>Edit Country</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2, fontFamily: 'Inter, sans-serif', color: '#475569' }}>Change country for {editCountryDialog.client?.email}</Typography>
          <FormControl fullWidth margin="normal">
            <InputLabel>Country</InputLabel>
            <Select value={editCountryDialog.countryCode} label="Country" onChange={handleEditCountryChange}>
              {countries.map((c) => <MenuItem key={c.code} value={c.code}>{c.name} (+{c.callingCode})</MenuItem>)}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditCountryDialog({ open: false, client: null, countryCode: '1' })}>Cancel</Button>
          <Button onClick={handleEditCountrySave} variant="contained" sx={{ bgcolor: '#4b2bee', '&:hover': { bgcolor: '#3d1fd4' } }}>Save</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmDialog.open} onClose={() => setConfirmDialog(p => ({ ...p, open: false }))}>
        <DialogTitle sx={{ fontFamily: 'Manrope, sans-serif', fontWeight: 700 }}>{confirmDialog.title}</DialogTitle>
        <DialogContent><Typography sx={{ fontFamily: 'Inter, sans-serif', color: '#475569' }}>{confirmDialog.message}</Typography></DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog(p => ({ ...p, open: false }))}>Cancel</Button>
          <Button onClick={confirmDialog.onConfirm} variant="contained" sx={{ bgcolor: '#4b2bee', '&:hover': { bgcolor: '#3d1fd4' } }}>Confirm</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={smsLimitDialog.open} onClose={() => setSmsLimitDialog({ open: false, client: null, limit: '', multiplier: '10' })} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontFamily: 'Manrope, sans-serif', fontWeight: 700 }}>Set Cost Limit</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2, fontFamily: 'Inter, sans-serif', color: '#475569' }}>Monthly cost limit for {smsLimitDialog.client?.email}</Typography>
          <TextField fullWidth type="number" label="Monthly Cost Limit ($)" value={smsLimitDialog.limit} onChange={(e) => setSmsLimitDialog(s => ({ ...s, limit: e.target.value }))} helperText="Leave empty for unlimited" placeholder="e.g. 100" sx={{ mb: 2 }} InputProps={{ inputProps: { min: 0, step: 0.01 } }} />
          <TextField fullWidth type="number" label="Cost Multiplier" value={smsLimitDialog.multiplier} onChange={(e) => setSmsLimitDialog(s => ({ ...s, multiplier: e.target.value }))} helperText="Twilio cost × multiplier = client-facing cost (default: 10x)" placeholder="10" InputProps={{ inputProps: { min: 0.1, step: 0.1 } }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSmsLimitDialog({ open: false, client: null, limit: '', multiplier: '10' })}>Cancel</Button>
          <Button onClick={handleSmsLimitSave} variant="contained" sx={{ bgcolor: '#4b2bee', '&:hover': { bgcolor: '#3d1fd4' } }}>Save</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={importDialogOpen} onClose={() => setImportDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontFamily: 'Manrope, sans-serif', fontWeight: 700 }}>Import Existing Client</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2, fontFamily: 'Inter, sans-serif', color: '#475569' }}>Link an existing Twilio subaccount to a new client user.</Typography>
          <TextField fullWidth margin="normal" label="Email" value={importForm.email} onChange={(e) => setImportForm(f => ({ ...f, email: e.target.value }))} required />
          <TextField fullWidth margin="normal" label="Twilio Phone Number" value={importForm.twilioNumber} onChange={(e) => setImportForm(f => ({ ...f, twilioNumber: e.target.value }))} placeholder="+17787177909" required />
          <TextField fullWidth margin="normal" label="Subaccount SID" value={importForm.subaccountSid} onChange={(e) => setImportForm(f => ({ ...f, subaccountSid: e.target.value }))} placeholder="AC..." required />
          <TextField fullWidth margin="normal" label="Auth Token" type="password" value={importForm.authToken} onChange={(e) => setImportForm(f => ({ ...f, authToken: e.target.value }))} required />
          <FormControl fullWidth margin="normal">
            <InputLabel>Country</InputLabel>
            <Select value={importForm.countryCode} label="Country" onChange={(e: SelectChangeEvent<string>) => setImportForm(f => ({ ...f, countryCode: e.target.value }))}>
              {countries.map((c) => <MenuItem key={c.code} value={c.code}>{c.name} (+{c.callingCode})</MenuItem>)}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportDialogOpen(false)}>Cancel</Button>
          <Button onClick={async () => {
            try {
              setImportLoading(true);
              // Convert ISO code to callingCode for the API
              const importCountry = countries.find(c => c.code === importForm.countryCode);
              const importCallingCode = importCountry ? importCountry.callingCode : '1';
              const res = await axios.post('/api/admin/clients/import', { ...importForm, countryCode: importCallingCode });
              showSnackbar(res.data.message || 'Client imported!', 'success');
              setImportDialogOpen(false);
              setImportForm({ email: '', twilioNumber: '', countryCode: 'CA', subaccountSid: '', authToken: '' });
              loadClients();
            } catch (e: any) { showSnackbar(e.response?.data?.error || 'Failed to import', 'error'); }
            finally { setImportLoading(false); }
          }} variant="contained" disabled={importLoading || !importForm.email || !importForm.twilioNumber || !importForm.subaccountSid || !importForm.authToken}
            sx={{ bgcolor: '#4b2bee', '&:hover': { bgcolor: '#3d1fd4' } }}
          >{importLoading ? <CircularProgress size={24} /> : 'Import'}</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar(s => ({ ...s, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert onClose={() => setSnackbar(s => ({ ...s, open: false }))} severity={snackbar.severity} sx={{ width: '100%' }}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default AdminDashboard;
